import type { ExplicitContextItem } from '@protoss/shared';
import { Select, SelectItem } from '@protoss/ui';
import * as React from 'react';
import { ResizableDivider } from '../../../components/ResizableDivider';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { formatBytes } from '../../../lib/format';
import { useRepoBrowser } from '../hooks/useRepoBrowser';
import type { RepoBrowserTreeNode } from '../types/source-system';
import { RepoFileViewer } from './RepoFileViewer';
import { RepoTreeExplorer } from './RepoTreeExplorer';

interface SourceSystemRepoBrowserProps {
  projectUuid: string;
  refreshKey: string | null;
  /**
   * Called when the user explicitly selects a repo node (file or directory) or
   * adds a Monaco line-range selection to AI context.
   * Optional - no context capture when omitted.
   */
  onSetDraftExplicitContext?: (item: ExplicitContextItem) => void;
}

const MIN_TREE_WIDTH = 240;
const MAX_TREE_WIDTH = 520;
const DEFAULT_TREE_WIDTH = 320;

export function SourceSystemRepoBrowser({
  projectUuid,
  refreshKey,
  onSetDraftExplicitContext,
}: SourceSystemRepoBrowserProps) {
  const {
    tree,
    treeLoading,
    treeError,
    selectedNode,
    selectedNodeId,
    selectedFile,
    fileLoading,
    fileError,
    expandedNodeIds,
    editorSelection,
    clearSelection,
    selectNode,
    toggleNode,
    updateEditorSelection,
    refresh: refreshTree,
    refetchFile,
  } = useRepoBrowser(projectUuid, refreshKey);

  const handleRetryFile = React.useCallback(() => {
    if (selectedNodeId) {
      void refetchFile(selectedNodeId);
    }
  }, [selectedNodeId, refetchFile]);

  const [treeWidth, setTreeWidth] = React.useState(DEFAULT_TREE_WIDTH);
  const [selectedRepoId, setSelectedRepoId] = React.useState<string | null>(null);

  // Auto-select the first repo when the tree loads
  React.useEffect(() => {
    if (tree && tree.roots.length > 0 && !selectedRepoId) {
      setSelectedRepoId(tree.roots[0]?.resourceUuid ?? null);
    }
  }, [tree, selectedRepoId]);

  const repoOptions = React.useMemo(() => {
    if (!tree) return [];
    return tree.roots
      .filter((r) => r.kind === 'repo')
      .map((r) => ({ value: r.resourceUuid, label: r.repoName }));
  }, [tree]);

  const activeRoots = React.useMemo(() => {
    if (!tree) return [];
    if (!selectedRepoId) return tree.roots;
    return tree.roots.filter((r) => r.resourceUuid === selectedRepoId);
  }, [tree, selectedRepoId]);

  const handleRepoChange = React.useCallback(
    (resourceUuid: string) => {
      setSelectedRepoId(resourceUuid);
      clearSelection();
    },
    [clearSelection]
  );

  const handleResize = React.useCallback((delta: number) => {
    setTreeWidth((current) => Math.min(MAX_TREE_WIDTH, Math.max(MIN_TREE_WIDTH, current + delta)));
  }, []);

  // ── Explicit context capture ──────────────────────────────────────────────
  // When a node is selected in the tree, notify the parent so it can add the
  // repo item to the AI chat's explicit context chip bar.
  const handleNodeSelect = React.useCallback(
    (node: RepoBrowserTreeNode) => {
      void selectNode(node);
      if (!onSetDraftExplicitContext) return;
      if (node.kind === 'file') {
        onSetDraftExplicitContext({
          id: crypto.randomUUID(),
          domain: 'repo',
          contextType: 'repo_file',
          projectUuid,
          repoId: node.repoId,
          repoName: node.repoName,
          path: node.path,
          fileName: node.name,
          displayLabel: node.name,
          source: 'explicit_user_selection',
        });
      } else if (node.kind === 'directory') {
        onSetDraftExplicitContext({
          id: crypto.randomUUID(),
          domain: 'repo',
          contextType: 'repo_directory',
          projectUuid,
          repoId: node.repoId,
          repoName: node.repoName,
          path: node.path,
          fileName: node.name,
          displayLabel: node.repoName + (node.path ? `/${node.path}` : ''),
          source: 'explicit_user_selection',
        });
      }
    },
    [selectNode, onSetDraftExplicitContext, projectUuid]
  );

  // Auto-create a draft chip whenever Monaco has a non-empty selection.
  // Fires on every selection change; skips cursor-only positions (no text selected).
  React.useEffect(() => {
    if (!onSetDraftExplicitContext || !editorSelection || !selectedFile) return;
    const { startLineNumber, startColumn, endLineNumber, endColumn } = editorSelection;
    // Skip pure cursor position (start === end means nothing is selected)
    if (startLineNumber === endLineNumber && startColumn === endColumn) return;
    onSetDraftExplicitContext({
      id: `${projectUuid}:repo:${selectedFile.nodeId}:lines:${startLineNumber}:${endLineNumber}`,
      domain: 'repo',
      contextType: 'repo_file_range',
      projectUuid,
      repoId: selectedFile.repoId,
      repoName: selectedFile.repoName,
      path: selectedFile.path,
      fileName: selectedFile.name,
      lineStart: startLineNumber,
      lineEnd: endLineNumber,
      displayLabel:
        startLineNumber === endLineNumber
          ? `${selectedFile.name} (line ${startLineNumber})`
          : `${selectedFile.name} (lines ${startLineNumber}–${endLineNumber})`,
      source: 'explicit_user_selection',
    });
  }, [editorSelection, selectedFile, onSetDraftExplicitContext, projectUuid]);

  return (
    <section className="ss-browser" aria-label="Repository browser">
      <div className="ss-browser__layout">
        <aside className="ss-browser__sidebar" style={{ width: `${treeWidth}px` }}>
          <div className="ss-browser__sidebar-header">
            <h2 className="ss-section__title">Repository</h2>
            {repoOptions.length > 0 && (
              <div className="ss-browser__repo-selector">
                <Select
                  value={selectedRepoId ?? ''}
                  onValueChange={handleRepoChange}
                  placeholder="Select repo"
                  size="sm"
                >
                  {repoOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            )}
          </div>

          <div className="ss-browser__sidebar-body">
            {treeLoading ? (
              <div className="ss-browser__empty">Loading repository snapshot…</div>
            ) : treeError ? (
              <div className="ss-browser__empty ss-browser__empty--error">
                <PageErrorState message={treeError} onRetry={refreshTree} />
              </div>
            ) : !tree || tree.roots.length === 0 ? (
              <div className="ss-browser__empty">
                No ingested repository snapshot is available yet. Save repo settings and run a sync.
              </div>
            ) : (
              <RepoTreeExplorer
                roots={activeRoots}
                expandedNodeIds={expandedNodeIds}
                selectedNodeId={selectedNodeId}
                onSelectNode={handleNodeSelect}
                onToggleNode={toggleNode}
              />
            )}
          </div>
        </aside>

        <ResizableDivider
          direction="vertical"
          onResize={handleResize}
          className="ss-browser__divider"
          ariaLabel="Resize repository tree"
        />

        <div className="ss-browser__viewer-pane">
          <div className="ss-browser__viewer-header">
            {selectedFile ? (
              <>
                <h2 className="ss-section__title ss-browser__viewer-title">{selectedFile.path}</h2>
                <p className="ss-browser__subtitle">
                  readonly ({formatBytes(selectedFile.sizeBytes)})
                </p>
              </>
            ) : (
              <>
                <h2 className="ss-section__title">Read Only</h2>
                <p className="ss-browser__subtitle">open a file from the tree</p>
              </>
            )}
          </div>

          <RepoFileViewer
            file={selectedFile}
            loading={fileLoading}
            error={fileError}
            selectedNodeKind={selectedNode?.kind ?? null}
            onSelectionChange={updateEditorSelection}
            onRetry={handleRetryFile}
          />
        </div>
      </div>
    </section>
  );
}
